import { createContext, useContext, useState, useEffect } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile
} from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";

// ── حساب الأيام المتبقية وتحديث الحساب إذا انتهى ──
async function checkSubscription(uid, data) {
  if (
    data?.subscription === "paid" &&
    data?.status === "active" &&
    data?.subscriptionEndDate
  ) {
    const endDate = data.subscriptionEndDate.toDate
      ? data.subscriptionEndDate.toDate()
      : new Date(data.subscriptionEndDate);

    const now = new Date();
    const diffMs = endDate - now;
    const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (daysLeft <= 0) {
      // انتهى الاشتراك — حوّله لمجاني
      await updateDoc(doc(db, "users", uid), {
        subscription: "free",
        status: "expired"
      });
      return { ...data, subscription: "free", status: "expired", daysLeft: 0 };
    }

    return { ...data, daysLeft };
  }
  return { ...data, daysLeft: null };
}

// ── Check if doctor account has expired ──
async function checkDoctorExpiration(uid, data) {
  if (
    data?.role === "doctor" &&
    !data?.isPermanent &&
    data?.accountExpiresAt &&
    !data?.suspended
  ) {
    const expiresAt = data.accountExpiresAt.toDate
      ? data.accountExpiresAt.toDate()
      : new Date(data.accountExpiresAt);

    const now = new Date();
    const diffMs = expiresAt - now;
    const accountDaysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (accountDaysLeft <= 0) {
      await updateDoc(doc(db, "users", uid), {
        suspended: true,
        status: "expired"
      });
      return { ...data, suspended: true, status: "expired", accountDaysLeft: 0 };
    }

    return { ...data, accountDaysLeft };
  }
  return { ...data, accountDaysLeft: data?.isPermanent ? null : null };
}

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        try {
          const docSnap = await getDoc(doc(db, "users", firebaseUser.uid));
          if (docSnap.exists()) {
            let data = docSnap.data();

            // Check subscription for parents
            data = await checkSubscription(firebaseUser.uid, data);

            // Check doctor account expiration
            if (data.role === "doctor") {
              data = await checkDoctorExpiration(firebaseUser.uid, data);
            }

            setProfile(data);

            // Update lastLogin timestamp
            await updateDoc(doc(db, "users", firebaseUser.uid), {
              lastLogin: serverTimestamp()
            }).catch(() => {}); // silent fail
          }
        } catch (e) {
          console.error("Profile fetch error:", e);
        }
      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  async function login(email, password) {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const docSnap = await getDoc(doc(db, "users", cred.user.uid));
    if (docSnap.exists()) {
      let data = docSnap.data();
      data = await checkSubscription(cred.user.uid, data);

      if (data.role === "doctor") {
        data = await checkDoctorExpiration(cred.user.uid, data);
      }

      setProfile(data);

      // Update lastLogin
      await updateDoc(doc(db, "users", cred.user.uid), {
        lastLogin: serverTimestamp()
      }).catch(() => {});
    }
    return cred;
  }

async function register({ email, password, name, role = "parent", phone, childAge, childName }) {    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName: name });
    const userData = {
      uid: cred.user.uid,
      name,
      email,
      role,
      phone: phone || null,
      status: "active",
      subscription: role === "parent" ? "free" : null,
      childAge: childAge || null,
      childName: childName || null,
      isADHD: false,
      adhdProfile: null,
      createdAt: serverTimestamp()
    };
    await setDoc(doc(db, "users", cred.user.uid), userData);
    setProfile(userData);
    return cred;
  }

  async function logout() {
    await signOut(auth);
    setUser(null);
    setProfile(null);
  }

  async function refreshProfile() {
    if (!user) return;
    const docSnap = await getDoc(doc(db, "users", user.uid));
    if (docSnap.exists()) {
      let data = docSnap.data();
      data = await checkSubscription(user.uid, data);
      if (data.role === "doctor") {
        data = await checkDoctorExpiration(user.uid, data);
      }
      setProfile(data);
    }
  }

  const isAdmin = profile?.role === "admin";
  const isDoctor = profile?.role === "doctor";

  return (
    <AuthContext.Provider value={{ user, profile, loading, login, register, logout, refreshProfile, isAdmin, isDoctor }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
